import React, { useEffect } from "react";

import { ReactComponent as DragIcon } from "@common/assets/images/drag.svg";
import Tooltip from "@common/components/Tooltip/Tooltip";
import { Row } from "@tanstack/react-table";
import { useDrag, useDragLayer } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";

import { BaseCell } from "./BaseCell";
import { Grab } from "./BaseTableStyles";
import { DRAG_TYPE_ROW } from "./TicketTitleCell";

interface Props<TData> {
  row: Row<TData>;
  tooltip?: string;
}

// The TicketTitleCell drag overlay spans the whole row and swallows hover events, so the handle
// sits above it (zIndex 3, below the sticky header at 4) and acts as its own drag source for the same row.
export function DragHandleCell<TData>({ row, tooltip }: Props<TData>) {
  const [, dragRef, previewRef] = useDrag({
    item: () => row,
    type: DRAG_TYPE_ROW,
  });

  // While another row is dragged, let events fall through to the TicketTitleCell drop target underneath.
  // Never on the dragged row's own handle: changing it during dragstart makes Chrome cancel the drag.
  // One subscription so both conditions update in the same render (React 17 doesn't batch native events).
  const { isAnyDragging, passThrough } = useDragLayer((monitor) => ({
    isAnyDragging: monitor.isDragging(),
    passThrough: monitor.isDragging() && (monitor.getItem() as Row<TData> | null)?.id !== row.id,
  }));

  // TicketTitleCell renders the drag preview
  useEffect(() => {
    previewRef(getEmptyImage(), { captureDraggingState: true });
  }, [previewRef]);

  return (
    <BaseCell
      noGradient
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        maxHeight: "4.8rem",
      }}
    >
      <Tooltip
        text={tooltip ?? ""}
        disabled={!tooltip || isAnyDragging}
        position="top"
        portal
        style={{ position: "relative", zIndex: 3, width: "auto", pointerEvents: passThrough ? "none" : undefined }}
      >
        <Grab ref={dragRef}>
          <DragIcon style={{ cursor: "grab" }} />
        </Grab>
      </Tooltip>
    </BaseCell>
  );
}
